/* eslint-disable import/prefer-default-export */
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import assign from 'lodash/assign';
import findIndex from 'lodash/findIndex';
import moment from 'moment';
import { performanceNodeTabs } from '@/constants/performance';
import topologyMapApi from '@/api/topologymap/common';
import treeApi from '@/api/common/tree';
import BookmarkApi from '@/api/frame/bookmark';
import { TREE_TYPE_DELIMETER, treeType } from '@/constants/tree';
import msgUtil from '@/utils/message';
import { getDate } from '@/utils/date';
import { convertObjectToQueryString } from '@/utils/common';
import { menuDefine } from '@/constants/menu';

/**
 * 링크를 통해 요청된 라우트 여부에 대해 설정이 필요한지 판단한다.
 * 논리/물리 그룹 트리 기준으로 전환된 경우에만 해당한다.
 * @author Ju
 * @param  {[type]} routeParams [description]
 * @return {[type]}             [description]
 */
function requireSetLinkRoute(routeParams) {
  return has(routeParams, 'treeKey') && typeof routeParams.treeKey !== 'number' && (routeParams.treeKey.indexOf(treeType.LOGICAL) >= 0 || routeParams.treeKey.indexOf(treeType.PHYSICAL) >= 0);
}

/**
 * 파라미터로 전달한 데이터 기준으로 라우팅한다.
 * @author Ju
 * @param  {[type]} vueCmp  [필수] 호출한 vue 컴포넌트
 * @param  {[type]} menu    [필수] 메뉴 아이디 (@/constans/menu의 menuDefine 참조 할 것.)
 * @param  {[type]} treeKey [필수] 트리키
 * @param  {[type]} tab     활성화 탭 정보
 */
/* eslint-disable no-param-reassign */
export function routePath(vueCmp, menu, treeKey, tab, newBrowserTab = false, params = null, noLinkRoute = false) {
  let path = `/${menu}`;
  const routeParams = {};
  if (!isNil(treeKey)) {
    path = `${path}/${treeKey}`;
    routeParams.treeKey = treeKey;
  }
  if (!isNil(tab) && tab !== '') {
    path = `${path}/${tab}`;
    routeParams.activeTab = tab;
  }
  if (newBrowserTab) {
    if (!noLinkRoute && requireSetLinkRoute(routeParams)) {
      if (isEmpty(params)) {
        params = {};
      }
      params.isLinkRoute = true;
    }
    if (!isEmpty(params)) {
      const queryString = convertObjectToQueryString(params);
      if (!isNil(queryString) && queryString !== '') {
        path = `${path}?${queryString}`;
      }
    }
    window.open(`/#${path}`, '_blank');
  } else {
    const route = {
      path,
      params: isEmpty(params) ? routeParams : assign(params, routeParams),
    };
    if (!noLinkRoute && requireSetLinkRoute(routeParams)) {
      vueCmp.$store.dispatch('setIsLinkRoute', true);
    }
    vueCmp.$router.push(route);
  }
}
/* eslint-enable no-param-reassign */

/**
 * [routeMenu description]
 * @author Ju
 * @param  {[type]}  vueCmp                [description]
 * @param  {[type]}  menu                  [description]
 * @param  {Boolean} [newBrowserTab=false] [description]
 * @param  {[type]}  [params=null]         [description]
 * @return {[type]}                        [description]
 */
export function routeMenu(vueCmp, menu, newBrowserTab = false, params = null) {
  routePath(vueCmp, menu, null, null, newBrowserTab, params);
}

/**
 * 파라미터로 전달한 관리 대상과 메뉴 기준으로 라우팅한다.
 * @author Ju
 * @param  {Object} vueCmp        [필수] 호출한 vue 컴포넌트
 * @param  {Number} objId         [필수] 관리대상의 objId
 * @param  {String} menu          [필수] 메뉴 아이디 (@/constans/menu의 menuDefine 참조 할 것.)
 * @param  {[type]} tab           활성화 탭 정보
 * @param  {[type]} managegroupId 관리 대상의 관리 그룹 아이디
 * @param  {String} _treeType     트리 타입
 * @param  {Boolean} newBrowserTab 새 브라우저 탭으로 제공 여부
 * @param  {Object} params  파라미터 객체
 * @return {Object} Promise객체
 */
export function routePathByObjId(vueCmp, objId, menu, tab, managegroupIds, _treeType, newBrowserTab = false, params = null) {
  return new Promise((resolve, reject) => {
    if (menuDefine.TOPOLOGYMAP === menu) {
      const target = '해당 장비의 토폴로지맵 정보';
      const action = msgUtil.action.VIEW;

      topologyMapApi
        .getMaplistByObjId(objId)
        .then((res) => {
          const result = res.data;
          if (result.success === true) {
            if (isEmpty(result.data)) {
              vueCmp.$message(msgUtil.getCustomMsgData('해당 장비의 토폴로지맵 정보가 없습니다.'));
              resolve(true);
            } else {
              const map = result.data[0];
              routePath(vueCmp, menu, map.mapId, objId, newBrowserTab, params);
              resolve(true);
            }
          } else {
            vueCmp.$message(msgUtil.getResultMsgData(action, target, false));
            reject(false);
          }
        })
        .catch(() => {
          vueCmp.$message(msgUtil.getResultMsgData(action, target, false));
          reject(false);
        });
    } else {
      const target = '장비 정보';
      const action = msgUtil.action.VIEW;
      treeApi
        .getTreeKey(objId, managegroupIds, _treeType)
        .then((response) => {
          const result = response.data;
          if (result.success === true && !isEmpty(result.data)) {
            routePath(vueCmp, menu, result.data, tab, newBrowserTab, params);
            resolve(true);
          } else {
            vueCmp.$message(msgUtil.getResultMsgData(action, target, false));
            reject(false);
          }
        })
        .catch(() => {
          vueCmp.$message(msgUtil.getResultMsgData(action, target, false));
          reject(false);
        });
    }
  });
}

/**
 * 라우팅 시 이벤트 시각 정보를 시간 필터 조건으로 설정한다.
 * @author Ju
 * @param  {[type]} store          [description]
 * @param  {[type]} checkDateteime [description]
 */
export function setRoutingEventDatetime(store, checkDateteime) {
  const datetime = getDate(checkDateteime);
  if (!isNil(getDate)) {
    const startDatetime = moment(datetime)
      .subtract(12, 'h')
      .toDate();
    const endDatetime = moment(datetime)
      .add(12, 'h')
      .toDate();
    store.dispatch('setRoutingDatetimeRange', {
      type: 'absolute',
      startDatetime,
      endDatetime,
    });
    store.dispatch('setInitialPlotLineDatetime', datetime);
  }
}

/**
 * 파라미터로 전달한 관리 대상과 메뉴 기준으로 라우팅한다.
 * @author Ju
 * @param  {Object} vueCmp        [필수] 호출한 vue 컴포넌트
 * @param  {Number} objId         [필수] 관리대상의 objId
 * @param  {Array} managegroupId 관리 대상의 관리 그룹 아이디
 * @param {Number} checkDatetime 이벤트 발생 시각
 * @param  {String} treeType     트리 타입
 * @param  {Boolean} newBrowserTab 새 브라우저 탭으로 제공 여부
 * @return {Object} Promise객체
 */
export function routePathByObjEventHist(vueCmp, objId, managegroupIds, checkDatetime, _treeType, newBrowserTab = false, params = {}) {
  const datetime = getDate(checkDatetime);
  if (newBrowserTab) {
    params.eventDatetime = datetime.getTime();
  } else {
    setRoutingEventDatetime(vueCmp.$store, datetime);
  }
  return routePathByObjId(vueCmp, objId, menuDefine.PERF_OPER, performanceNodeTabs.analysis.name, managegroupIds, _treeType, newBrowserTab, params);
}

/**
 * 특정 맵으로 라우팅을 변경한다.
 * (mixins/topologymap/base.js → routeMap)
 * @author Ju
 * @param  {Number} mapId  맵 아이디
 * @param  {Object} params 전달 할 데이터
 */
export function routeMap(vueCmp, mapId, params) {
  vueCmp.$router.push({
    path: `/${menuDefine.TOPOLOGYMAP}/${mapId}`,
    query: params,
  });
}

/**
 * 라우트 경로 이력 핸들러 타이머를 시작한다.
 * 타이머 콜백 수행 시점에 현재 경로가 타이머 시작 시점 경로와 동일할 경우 해당 경로 기준으로 자주 방문하는 경로 정보를 업데이트한다.
 * @author Ju
 * @param  {Object} vueCmp 호출하는 Vue컴포넌트 객체
 * @param  {String} path   라우트 경로
 * @return {Number}        라우트 경로 이력 핸들러 타이머
 */
export function startRoutePathHistHandlerTimer(vueCmp, path) {
  return setTimeout(() => {
    if (path === vueCmp.$route.path) {
      const menu = vueCmp.$store.getters.selectedMenu;
      const params = {
        path,
        pathDesc: vueCmp.$store.getters.navigationPathLabel,
        menuId: menu.menuId,
        managetypeId: menu.managetypeId,
      };
      BookmarkApi.updateFrequentlyVisitPath(params);
    }
  }, 3000);
}

/**
 * 라우트 경로 이력 핸들러 타이머를 제거한다.
 * @author Ju
 * @param  {Number} timer 라우트 경로 이력 핸들러 타이머
 */
export function clearRoutePathHistHandlerTimer(timer) {
  if (!isNil(timer)) {
    clearTimeout(timer);
  }
}

/**
 * 라우트된 트리키에서 트리 타입을 찾아 반환한다.
 * @author Ju
 * @param  {String} routeTreeKey 라우트 트리키
 * @return {String} 트리 타입. 없을 경우 null을 반환한다.
 */
export function getTreeTypeFromTreeKey(routeTreeKey) {
  const delimeterIndex = routeTreeKey.indexOf(TREE_TYPE_DELIMETER);
  return delimeterIndex >= 0 ? routeTreeKey.substring(0, delimeterIndex) : null;
}

/**
 * 라우트된 트리키에서 트리 타입을 제외한 트리키를 반환한다.
 * @author Ju
 * @param  {String} routeTreeKey 라우트 트리키
 * @return {String} 트리 타입이 제거된 트리키
 */
export function getTreeKeyExceptTreeType(routeTreeKey) {
  const delimeterIndex = routeTreeKey.indexOf(TREE_TYPE_DELIMETER);
  return delimeterIndex >= 0 ? routeTreeKey.substring(delimeterIndex + 1) : routeTreeKey;
}
/**
 * 이동할  경로가 없음을 알리는 메시지를 제공한다.
 * @author Ju
 * @param  {[type]} vueCmp [description]
 * @return {[type]}        [description]
 */
function showNoPathMessage(vueCmp) {
  vueCmp.$message(msgUtil.getCustomMsgData('이동할 경로 정보가 없습니디.'));
}

/**
 * 존재하는 경로인지 확인 후 해당 경로로 화면을 전환한다.
 * @author Ju
 * @param  {Object} vueCmp             Vue컴포넌트 객체
 * @param  {String} path               이동할 경로
 * @param  {[type]} handlerInvalidPath 유효하지 않는 경로일 경우 핸들러
 */
export function routeByPathAfterCheckValid(vueCmp, path, handlerInvalidPath, newBrowserTab = false) {
  if (!isNil(path) || path !== '') {
    const pathArr = path.substring(1).split('/');
    const pathArrLength = pathArr.length;
    if (pathArrLength > 0) {
      if (findIndex(vueCmp.$store.getters.rootMenuInfo, (item) => item.url === pathArr[0]) < 0) {
        showNoPathMessage(vueCmp);
        return;
      }
      const menuUrl = pathArr[0];
      if (pathArrLength === 1) {
        routePath(vueCmp, menuUrl, null, null, newBrowserTab);
      } else {
        const routeTreeKey = pathArr[1];
        const routeTreeType = menuUrl === menuDefine.TOPOLOGYMAP ? 'topologymap' : getTreeTypeFromTreeKey(routeTreeKey); // TODO Ju 토폴로지맵은 아직 트리 타입을 관리하는 구조가 아니기 때문에 임시로 처리. 추후 수정 필요
        const treeKey = getTreeKeyExceptTreeType(routeTreeKey);
        BookmarkApi.checkValidTreeKey(routeTreeType, treeKey)
          .then((response) => {
            if (response.data.success === true) {
              if (response.data.data === true) {
                const tab = pathArrLength > 2 ? pathArr[2] : null;
                routePath(vueCmp, menuUrl, routeTreeKey, tab, newBrowserTab, null, true); // 즐겨잧기나 자주 가는 화면을 통해 이동할 경우에는 특정 Obj 기준으로 조건 필터링이 되지 않도록 한다.
              } else {
                handlerInvalidPath(path);
              }
            } else {
              vueCmp.$message(msgUtil.getCustomResultMsgData('해당 경로로 이동하지 못하였습니다.', false));
            }
          })
          .catch((e) => {
            vueCmp.$message(msgUtil.getCustomResultMsgData('해당 경로로 이동하지 못하였습니다.', false));
            throw new Error(e);
          });
      }
    } else {
      showNoPathMessage(vueCmp);
    }
  } else {
    showNoPathMessage(vueCmp);
  }
}



// WEBPACK FOOTER //
// ./src/utils/router.js